'use client';

import { fetchPromotions } from '@eliadmin/core/src/api/promoptions/promotions-api';
import {
  getNextPromotionPage,
  getPromotionPath,
} from '@eliadmin/core/src/api/promoptions/promotion-utils';
import Link from '@eliadmin/core/src/components/link';
import { useActivePromotions } from '@eliadmin/core/src/components/promotions/use-active-promotions';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

/** Advisory offers only. The backend applies and prices them after cart mutations. */
export function ProductPromotions() {
  const locale = useLocaleStore(state => state.currentLocale);
  const product = useVariantsStore(state => state.product);
  const variant = useVariantsStore(state => state.selectedVariant);
  const me = useCustomerStore(state => state.me);
  const initialized = useCustomerStore(state => state.isInitialized);
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslations();
  useEffect(() => setMounted(true), []);
  const query = useInfiniteQuery({
    queryKey: [
      'product-promotions-v2',
      process.env.NEXT_PUBLIC_CURRENT_STORE,
      locale,
      product.id,
      variant?.id ?? null,
      me?.customer.id ?? null,
    ],
    enabled:
      mounted &&
      initialized &&
      !!product.id &&
      product.status !== 'ended' &&
      product.status !== 'arrival',
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      fetchPromotions({
        locale,
        authToken: me?.authToken,
        signal,
        query: { product_id: product.id, variant_id: variant?.id, page: pageParam, results: 100 },
      }),
    getNextPageParam: getNextPromotionPage,
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
    throwOnError: false,
  });
  const offers = useMemo(
    () =>
      query.isError
        ? []
        : (query.data?.pages.flatMap(page => (Array.isArray(page?.data) ? page.data : [])) ?? []),
    [query.data, query.isError],
  );
  const active = useActivePromotions(offers);
  if (!mounted || !initialized || !active.length) return null;
  return (
    <ul className="ea-product-promotions flex w-full flex-col gap-2">
      {active.map(promotion => {
        const href = getPromotionPath(promotion.slug);
        return (
          <li
            key={promotion.id}
            className="flex gap-3 rounded-[18px] border border-[#FBDCDC] bg-[#FDF0F0] p-4"
          >
            <Icon name="percent" size={20} className="mt-0.5 shrink-0 text-[#D7191F]" />
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
              <p className="dlz-body-s-medium text-[#0F1012]">{promotion.name}</p>
              {promotion.description ? (
                <p className="dlz-body-s text-[#4F545C]">{promotion.description}</p>
              ) : null}
              {href ? (
                <Link
                  href={href}
                  prefetch={false}
                  className="dlz-body-s-medium rounded-sm text-[#D7191F] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
                >
                  {t('text1076')}
                </Link>
              ) : null}
            </div>
          </li>
        );
      })}
      {query.hasNextPage ? (
        <li>
          <Button
            variant="secondary"
            size="m"
            isLoading={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {t('text338')}
          </Button>
        </li>
      ) : null}
    </ul>
  );
}
